import { useRef, useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { canStartPull, pullState } from './pull-to-refresh';

/**
 * Tirer-pour-rafraîchir autour du Feed (natif et web mobile). La décision vit
 * dans `pull-to-refresh.ts` (pure, testée) ; ici on ne fait que la jouer.
 *
 * ⚠️ `touch-action: pan-y` reste en place : on n'empêche JAMAIS le défilement.
 * Le geste n'est pris que s'il part du haut de la page ET descend ; sinon le
 * doigt défile normalement. Le pointeur n'est pas capturé : on ne vole rien.
 */
export function PullToRefreshZone({
  onRefresh,
  isRefreshing,
  children,
}: {
  onRefresh: () => void | Promise<void>;
  isRefreshing: boolean;
  children: ReactNode;
}) {
  const startY = useRef<number | null>(null);
  const [pull, setPull] = useState({ offset: 0, armed: false });

  const scrollTop = () =>
    window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

  // Un espace de l'app (élève, prof…) ne fait pas défiler la page mais son
  // <main> : on lit le conteneur défilant le plus proche quand il y en a un.
  const containerTop = (el: HTMLElement | null) => {
    for (let node = el; node; node = node.parentElement) {
      if (node.scrollHeight > node.clientHeight + 1) return node.scrollTop;
    }
    return scrollTop();
  };

  const reset = () => {
    startY.current = null;
    setPull({ offset: 0, armed: false });
  };

  return (
    <div
      className="relative touch-pan-y"
      onTouchStart={(e) => {
        const touch = e.touches.length === 1 ? e.touches[0] : undefined;
        if (!touch) return;
        if (!canStartPull(containerTop(e.currentTarget), isRefreshing)) return;
        startY.current = touch.clientY;
      }}
      onTouchMove={(e) => {
        const touch = e.touches.length === 1 ? e.touches[0] : undefined;
        if (startY.current === null || !touch) return;
        setPull(pullState(touch.clientY - startY.current));
      }}
      onTouchEnd={() => {
        const fire = pull.armed; // seuil franchi pendant le geste
        reset();
        if (fire) void onRefresh();
      }}
      onTouchCancel={reset}
    >
      {/* Indicateur : apparaît sous le doigt, disparaît au relâchement. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 flex justify-center overflow-hidden"
        style={{ height: pull.offset }}
      >
        <RefreshCw
          className={cn(
            'text-primary-500 mt-2 h-5 w-5 transition-transform',
            pull.armed && 'scale-125',
            isRefreshing && 'animate-spin',
          )}
          style={{ transform: `rotate(${pull.offset * 3}deg)` }}
        />
      </div>
      <div
        style={{ transform: pull.offset ? `translateY(${pull.offset}px)` : undefined }}
        className={pull.offset ? '' : 'transition-transform duration-200'}
      >
        {children}
      </div>
    </div>
  );
}
